import { Reducer } from "preact/hooks";
import {
  FormState,
  FormStatus,
  FormValue,
  FormField,
  FormPage,
  FieldRecord,
  FileBlob,
} from "./FormTypes";
import { shouldDisplayField } from "./utils/conditionalLogic";
import {
  ErrorReport,
  FieldValidationState,
  shouldRegisterError,
} from "./utils/validation";
import { getVisiblePages } from "./utils/visiblePages";
import { isNewCheckboxParams } from "./components/common";

export type ValidationScope = "touched" | "all" | "page";

export type FormAction =
  | { type: "inputChange"; name: string; value: FormValue | null }
  | { type: "updateWorkflowState"; state: FormStatus }
  | {
      type: "validate";
      result: ErrorReport | null;
      scope: Extract<ValidationScope, "touched" | "all">;
    }
  | {
      type: "validate";
      result: ErrorReport | null;
      scope: Extract<ValidationScope, "page">;
      pageIdx: number;
    }
  | { type: "focusField"; key: string }
  | { type: "blurField" }
  | { type: "setPage"; page: FormPage }
  | { type: "setSubmitDisabled"; value: boolean }
  | { type: "validateUrlParams"; result: ErrorReport | null }
  | { type: "andFormIsValid"; value: boolean }
  | { type: "addFileBlob"; value: FileBlob }
  | { type: "removeFileBlob"; value: FileBlob };

export const formReducer: Reducer<FormState, FormAction> = (
  state: FormState,
  action: FormAction
): FormState => {
  switch (action.type) {
    case "setPage":
      return { ...state, currentPage: action.page };
    case "inputChange": {
      const { name, value } = action;

      if (value === null) {
        return { ...state };
      }

      const currentField = state.fields[name];

      let newValue: string | boolean | Set<string> | string[] | Date;

      //If the input is a multi-select, add or remove the value from the set
      // rather than replacing it.
      if (currentField.input_type == "multi-select") {
        let currentValue = currentField.currentValue ?? new Set<string>();

        const normalOptions = isNewCheckboxParams(currentField.params)
          ? currentField.params.options
          : currentField.params.schema.items.enum ?? [];

        const isNormalOption = normalOptions.includes(value as string);

        if (currentValue.has(value as string)) {
          currentValue.delete(value as string);
        } else {
          if (isNormalOption) {
            currentValue.add(value as string);
          }

          // Other option
          else {
            const values = [...currentValue];

            // Remove previous "other" values
            const filteredValues = values.filter((val) =>
              normalOptions.includes(val)
            );

            currentValue = new Set(filteredValues);
            currentValue.add(value as string);
          }
        }
        newValue = currentValue;
      } else if (currentField.block_type === "number") {
        newValue = value.toString().trim();
      } else {
        newValue = value;
      }

      const newFields = {
        ...state.fields,
        [name]: {
          ...currentField,
          status: "input",
          currentValue: newValue,
        } as FormField,
      };

      return { ...state, fields: newFields };
    }
    case "focusField": {
      // If an input has been focused, change its status so that we validate it when the user moves to a different field
      const field = state.fields[action.key];

      return {
        ...state,
        fields: {
          ...state.fields,
          [action.key]: { ...field, status: "input" },
        },
      };
    }
    case "validate": {
      // If the form is in preview mode, suppress validation errors for fields that are prefilled from url parameters.
      const suppressUrlValidation = state.livePreview;

      let errorsMap: {
        [key: string]: FieldValidationState | undefined;
      } = {};

      /** Initialise the validation status as valid
       */
      let formValidationStatus: "invalid" | "valid" = "valid";

      // Transform AJV errors into partial fields
      if (action.result) {
        errorsMap = Object.entries(action.result).reduce(
          (
            acc,
            [key, msgs]
          ): {
            [key: string]: FieldValidationState | undefined;
          } => {
            return {
              ...acc,
              [key]: {
                status: "invalid" as const,
                validationErrors: msgs.length
                  ? msgs
                  : ["Please enter a valid response."],
              },
            };
          },
          errorsMap
        );
      }

      // Reduce the states for existing fields, merging in errors if they exist.
      // If there's no error for a field, reset it to valid.
      const newFields: FieldRecord = Object.entries(state.fields).reduce(
        (acc, [key, field]): FieldRecord => {
          const fieldError = errorsMap[key];

          // While we're here, check if this field's visibility should change.
          // We do this at the same time as validation (ie. on field blur) so that visibility doesn't flash
          // in and out as the user types into a field on the same page.
          const fieldWithVisibility = {
            ...field,
            visible: shouldDisplayField(state.fields, field),
          };

          if (
            shouldRegisterError(
              action,
              suppressUrlValidation,
              fieldWithVisibility,
              fieldError
            )
          ) {
            formValidationStatus = "invalid";

            return { ...acc, [key]: { ...fieldWithVisibility, ...fieldError } };
          } else {
            return {
              ...acc,
              [key]: {
                ...fieldWithVisibility,
                validationErrors: null,
              },
            };
          }
        },
        {}
      );

      const visiblePages = getVisiblePages(state.pages, newFields);

      return {
        ...state,
        formState: { status: formValidationStatus },
        fields: newFields,
        visiblePages,
      };
    }
    case "updateWorkflowState": {
      return { ...state, formState: action.state };
    }
    case "setSubmitDisabled": {
      return { ...state, submitDisabled: action.value };
    }
    case "andFormIsValid": {
      // ANDs together the current value and the value passed in
      // ie, it can be set to invalid but never set back to valid
      return {
        ...state,
        formDefinitionIsValid: state.formDefinitionIsValid && action.value,
      };
    }
    case "validateUrlParams": {
      return {
        ...state,
        urlParamErrors: action.result ? { ...action.result } : {},
      };
    }
    case "addFileBlob": {
      const blobs = state.fileBlobs ?? {};
      blobs[action.value.key] = action.value;
      return {
        ...state,
        fileBlobs: { ...blobs },
      };
    }
    case "removeFileBlob": {
      const blobs = state.fileBlobs ?? {};
      delete blobs[action.value.key];
      return {
        ...state,
        fileBlobs: { ...blobs },
      };
    }
    default:
      return { ...state };
  }
};
